"use client"

import { Button } from "@/components/ui/button"
import { useDialogModal } from "@/hook/handle-modal/hooks/actions"
import { useUpdateInvoicePaymentStatusInBatchMutation } from "@/services/tax/tax-workflow"
import { Database, FileDown, FileEdit, FileText } from "lucide-react"
import { useSelectedItems } from "../../../context/selected-items-context"
import { GenerateGNREModal } from "../../modal/generate-gnre"
import { GenerateNffModal } from "../../modal/generate-nff"
import { PaymentConfirmationModal } from "../../modal/payment-confirmation"
import { InvoiceSyncModal } from "../../modal/invoice-sync"
import { GnreProgressModal } from "../../modal/gnre-progress"

const isDev = import.meta.env.VITE_REACT_ENV === "development" || import.meta.env.VITE_REACT_ENV === "homolog"

export function DataTableActions() {
  const { handleModal } = useDialogModal()
  const { selectedItemKeys, clearSelectedItems } = useSelectedItems()
  const [updateBatchPaymentStatus, { isLoading }] = useUpdateInvoicePaymentStatusInBatchMutation()

  const handleOpenGnreModal = () => {
    handleModal({
      isOpen: true,
      element: <GenerateGNREModal />,
      title: "Geração da GNRE",
    })
  }

  const handleOpenNffModal = () => {
    handleModal({
      isOpen: true,
      element: <GenerateNffModal />,
      title: "Geração de NFF",
      description: "Geração de Nota Fiscal Fácil",
    })
  }

  const handleOpenInvoiceSyncModal = () => {
    handleModal({
      isOpen: true,
      element: <InvoiceSyncModal />,
      title: "Sincronizar Invoices (Dev)",
      description: "Executar job de sincronizacao Oracle -> PostgreSQL",
    })
  }

  const handleOpenPaymentModal = () => {
    handleModal({
      isOpen: true,
      element: (
        <PaymentConfirmationModal
          selectedCount={selectedItemKeys.size}
          onConfirm={handleBatchPaymentUpdate}
          onCancel={() => handleModal({ isOpen: false })}
          isLoading={isLoading}
        />
      ),
      title: "Confirmar alteração de status",
    })
  }

  const handleBatchPaymentUpdate = async () => {
    try {
      const items = Array.from(selectedItemKeys).map((id) => ({
        id,
        is_payed: true,
      }))

      await updateBatchPaymentStatus(items)
      clearSelectedItems()
      handleModal({ isOpen: false }) // Close modal after success
    } catch (error) {
      console.error("Error updating payment status:", error)
    }
  }

  return (
    <div className="flex flex-col gap-4 mt-4 p-4 bg-[#F9FBFC] rounded-lg border w-full">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center flex-wrap gap-4">
          <Button variant={"outline"} className="text-sm items-center gap-2 h-9 hidden">
            <FileDown size={16} />
            Exportar CSV/XLSX
          </Button>
          {isDev && (
            <>
              <div className="h-6 w-px bg-gray-300" />
              <Button
                onClick={handleOpenInvoiceSyncModal}
                variant="outline"
                className="text-sm flex items-center gap-2 h-9"
              >
                <Database size={16} />
                Sync Invoices (Dev)
              </Button>
            </>
          )}
          <div className="h-6 w-px bg-gray-300" />

          {selectedItemKeys.size > 0 && (
            <>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-md border border-blue-200">
                <div className="w-2 h-2 bg-blue-500 rounded-full" />
                <span className="text-sm font-medium text-blue-700">
                  {selectedItemKeys.size} {selectedItemKeys.size !== 1 ? "itens" : "item"} selecionado
                  {selectedItemKeys.size !== 1 ? "s" : ""}
                </span>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-3">
          {selectedItemKeys.size > 0 && (
            <>
              <Button
                onClick={handleOpenPaymentModal}
                disabled={selectedItemKeys.size === 0}
                className="text-sm flex items-center gap-2 h-9"
                variant="default"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M9 12l2 2 4-4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                </svg>
                Marcar como pago
              </Button>
              <div className="h-6 w-px bg-gray-300" />
            </>
          )}

          <GnreProgressModal />

          <Button onClick={handleOpenGnreModal} className="text-sm flex items-center gap-2 h-9">
            <FileText size={16} />
            Gerar GNRE
          </Button>

          <div className="h-6 w-px bg-gray-300" />

          <Button onClick={handleOpenNffModal} className="text-sm flex items-center gap-2 h-9">
            <FileEdit size={16} />
            Gerar NFFs
          </Button>
        </div>
      </div>
    </div>
  )
}
